import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Upload, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WebsiteSettings = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    site_name: 'ত্রিপুরা দর্পণ',
    site_name_english: 'Tripura Darpan',
    logo_url: '',
    contact_email: 'news@tripuradarpan.com',
    contact_phone: '+91 381 234 5678',
    address: 'Agartala, Tripura',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    upi_id: '',
    upi_qr_code_url: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'logo');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSettings({ ...settings, logo_url: response.data.url });
      toast({ title: 'Success', description: 'Logo uploaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'upi');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSettings({ ...settings, upi_qr_code_url: response.data.url });
      toast({ title: 'Success', description: 'UPI QR code uploaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings, getAuthHeaders());
      toast({ title: 'Success', description: 'Settings saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Website Settings</h1>
          <p className="text-gray-600 mt-1">Configure your website</p>
        </div>
        <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700" disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Site Information */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="text-lg font-semibold mb-4">Site Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Site Name (Bengali)</Label>
                <Input
                  value={settings.site_name}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Site Name (English)</Label>
                <Input
                  value={settings.site_name_english}
                  onChange={(e) => setSettings({ ...settings, site_name_english: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Logo</Label>
              <Input type="file" accept="image/*" onChange={handleLogoUpload} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              {settings.logo_url && (
                <img src={settings.logo_url} alt="Logo" className="mt-2 h-16" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={settings.contact_phone}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Social Media */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
            <div>
              <Label>Facebook URL</Label>
              <Input
                value={settings.facebook_url}
                onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                placeholder="https://facebook.com/tripuradarpan"
              />
            </div>
            <div>
              <Label>Twitter URL</Label>
              <Input
                value={settings.twitter_url}
                onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                placeholder="https://twitter.com/tripuradarpan"
              />
            </div>
            <div>
              <Label>Instagram URL</Label>
              <Input
                value={settings.instagram_url}
                onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                placeholder="https://instagram.com/tripuradarpan"
              />
            </div>
          </CardContent>
        </Card>

        {/* UPI Payment Setup */}
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-green-600">UPI Payment Setup</Badge>
            </div>
            <h3 className="text-lg font-semibold">Subscription Payment Configuration</h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure UPI payment for e-paper subscriptions. Users will scan QR code to pay.
            </p>
            
            <div>
              <Label>UPI ID *</Label>
              <Input
                value={settings.upi_id}
                onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })}
                placeholder="yourname@paytm / yourname@phonepe"
              />
              <p className="text-xs text-gray-500 mt-1">Your UPI ID for receiving payments</p>
            </div>

            <div>
              <Label>Upload UPI QR Code *</Label>
              <Input type="file" accept="image/*" onChange={handleQRUpload} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading QR code...</p>}
              {settings.upi_qr_code_url && (
                <div className="mt-3 p-4 bg-white rounded-lg">
                  <p className="text-sm font-medium mb-2">Current QR Code:</p>
                  <img
                    src={settings.upi_qr_code_url}
                    alt="UPI QR Code"
                    className="w-48 h-48 border-2 border-gray-300 rounded"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Generate QR from any UPI app (PayTM, PhonePe, GPay) and upload screenshot
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">📱 How to get UPI QR Code:</p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Open any UPI app (PhonePe, PayTM, Google Pay)</li>
                <li>Go to "Receive Money" or "My QR Code"</li>
                <li>Take screenshot of your QR code</li>
                <li>Upload the screenshot here</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WebsiteSettings;